import React, { useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { flexRender, type Table, type ColumnSizingState } from '@tanstack/react-table';
import { BarChart3, ChevronDown, Code2, Copy, FileJson, FileSearch, Filter, Link2, List, MapPin, Pencil, Pin, X } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/ui/context-menu';
import { pinnedColumnBorderStyle } from '../hooks/useColumnPinning';
import { FIND_USAGES_MENU_LABEL } from '../../dependency-viewer/services/findUsagesMenu';

type GridHeaderRowProps = {
  table: Table<Record<string, unknown>>;
  columnSizing: ColumnSizingState;
  editMode?: { isEditMode: boolean; isColumnEditable: (id: string) => boolean };
  canSort: boolean;
  sortState: { columnId: string; direction: 'asc' | 'desc' } | null;
  setSortState: (state: { columnId: string; direction: 'asc' | 'desc' } | null) => void;
  isWindowed: boolean;
  handleWindowedSortChange: (state: { columnId: string; direction: 'asc' | 'desc' } | null) => void;
  lockColumnWidths: () => void;
  pinColumn: (id: string, position: 'left' | 'right') => void;
  unpinColumn: (id: string) => void;
  getPinnedLeftOffset: (id: string, sizing: ColumnSizingState, minWidth: number) => number;
  getPinnedRightOffset: (id: string, sizing: ColumnSizingState, minWidth: number) => number;
  isLastPinnedLeft: (id: string) => boolean;
  isFirstPinnedRight: (id: string) => boolean;
  showResultFilter: boolean;
  isFilterActive: boolean;
  columnFilters: Record<string, string>;
  globalFilter: string;
  setColumnFilters: (update: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  setGlobalFilter: (value: string) => void;
  filterInputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  minColumnWidth: number;
  maxAutoColumnWidth: number;
  isPopulating?: boolean;
  /**
   * Optional handler — when provided, renders the "Add to Filter" entry first,
   * as `TableExplorerGrid`'s header menu does (parity pair).
   */
  onAddToFilter?: (columnId: string) => void;
  /** Why "Add to Filter" is disabled for a column (shown as its tooltip), or null. Read when the menu opens. */
  addToFilterUnavailableReason?: (columnId: string) => string | null;
  /** Optional handler — when provided, renders a "Profile column" entry. */
  onProfileColumn?: (columnId: string) => void;
  /**
   * Optional handler — when provided, renders the "Find usages…" entry.
   * Parity pair: `TableExplorerGrid`'s header menu must carry the same item.
   */
  onFindUsages?: (columnId: string) => void;
  /** Column IDs whose loaded values look like JSON but aren't declared yet. */
  declarableJsonColumnIds?: ReadonlySet<string>;
  /** Handler for the "Declare as JSON" header context menu item. */
  onDeclareAsJson?: (columnId: string) => void;
  /** Column IDs that already have a JSON declaration. Drives the
   * "JSON details" entry (which replaces "Declare as JSON" on declared
   * columns). */
  declaredJsonColumnIds?: ReadonlySet<string>;
  /** Handler for the "JSON details" header context menu item. */
  onShowJsonDetails?: (columnId: string) => void;
  /** Column IDs eligible to be declared as enum columns. */
  declarableEnumColumnIds?: ReadonlySet<string>;
  /** Column IDs whose engine type is a native enum (MySQL ENUM / PG pg_enum). */
  nativeEnumColumnIds?: ReadonlySet<string>;
  /** Column IDs that already have an enum declaration. */
  declaredEnumColumnIds?: ReadonlySet<string>;
  /** Handler when the user picks an enum action from the header context menu.
   * `mode` distinguishes "declare from scratch" vs. "configure existing native
   * enum" vs. "show details for already-declared column". */
  onEnumColumnAction?: (
    columnId: string,
    mode: 'eligible' | 'native' | 'declared',
  ) => void;
  /** Column IDs that already have at least one loose-FK declaration —
   * drives the "Edit loose relationship" entry. */
  looseFkColumnIds?: ReadonlySet<string>;
  /** Column IDs eligible to declare a loose FK on (typically: has source
   * coords and not currently a real or loose FK). Drives the
   * "Declare loose relationship" entry. */
  looseFkEligibleColumnIds?: ReadonlySet<string>;
  /** Handler for the "Edit loose relationship" header context menu item. */
  onEditLooseFk?: (columnId: string) => void;
  /** Handler for the "Declare loose relationship" header context menu item. */
  onDeclareLooseFk?: (columnId: string) => void;
  /** Column IDs eligible to be declared as coordinate columns (T18). */
  declarableSpatialColumnIds?: ReadonlySet<string>;
  /** Column IDs that already have a coordinate declaration (T18). */
  declaredSpatialColumnIds?: ReadonlySet<string>;
  /** Handler when the user picks a coordinate action from the header context
   * menu — `state` distinguishes "declare from scratch" vs. "show details for an
   * already-declared column". */
  onSpatialColumnAction?: (columnId: string, state: 'declared' | 'declarable') => void;
  /** Handler for the "Copy values" header context menu item. Copies the
   * column's cell values from the currently loaded rows as a comma-separated
   * list. */
  onCopyColumnValues?: (columnId: string) => void;
  /**
   * PP-01 — column ids whose engine type is numeric. Their header label and
   * inline filter input are right-aligned so the digits column up with the
   * right-aligned cells below.
   */
  numericColumnIds?: ReadonlySet<string>;
};

export const GridHeaderRow = React.memo(function GridHeaderRow({
  table,
  columnSizing,
  editMode,
  canSort,
  sortState,
  setSortState,
  isWindowed,
  handleWindowedSortChange,
  lockColumnWidths,
  pinColumn,
  unpinColumn,
  getPinnedLeftOffset,
  getPinnedRightOffset,
  isLastPinnedLeft,
  isFirstPinnedRight,
  showResultFilter,
  isFilterActive,
  columnFilters,
  globalFilter,
  setColumnFilters,
  setGlobalFilter,
  filterInputRefs,
  minColumnWidth,
  maxAutoColumnWidth,
  isPopulating = false,
  onAddToFilter,
  addToFilterUnavailableReason,
  onProfileColumn,
  onFindUsages,
  declarableJsonColumnIds,
  onDeclareAsJson,
  declaredJsonColumnIds,
  onShowJsonDetails,
  declarableEnumColumnIds,
  nativeEnumColumnIds,
  declaredEnumColumnIds,
  onEnumColumnAction,
  looseFkColumnIds,
  looseFkEligibleColumnIds,
  onEditLooseFk,
  onDeclareLooseFk,
  declarableSpatialColumnIds,
  declaredSpatialColumnIds,
  onSpatialColumnAction,
  onCopyColumnValues,
  numericColumnIds,
}: GridHeaderRowProps) {
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [headerRowHeight, setHeaderRowHeight] = useState(0);
  // Measure the main header row height so the filter row's pinned <th>s can
  // stick at the correct vertical offset (directly below the header row).
  // Pinned <th>s need their own top offset because position:sticky children
  // do not inherit their parent <thead>'s sticky positioning.
  useLayoutEffect(() => {
    const el = headerRowRef.current;
    if (!el) return;
    const update = () => setHeaderRowHeight(el.getBoundingClientRect().height);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showResultFilter]);

  // Sticky is applied per-<th> (not on <thead>) because Chromium has a quirk
  // where a sticky <th> inside a sticky <thead> only honors its horizontal
  // offset; the vertical `top` anchor gets absorbed by the parent's sticky
  // containing block, so pinned headers scroll away with the rows.
  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup, groupIdx) => (
        <tr key={headerGroup.id} ref={groupIdx === 0 ? headerRowRef : undefined}>
          {headerGroup.headers.map((header) => {
            const hasExplicitWidth = columnSizing[header.id] !== undefined;
            const isEditableHeader = editMode?.isEditMode && editMode.isColumnEditable(header.id);
            const isNumericHeader = numericColumnIds?.has(header.id) ?? false;
            const isPinned = header.column.getIsPinned();
            const stickyStyle = isPinned === 'left' ? {
              position: 'sticky' as const,
              left: getPinnedLeftOffset(header.id, columnSizing, minColumnWidth),
              top: 0,
              zIndex: 30,
              ...(isLastPinnedLeft(header.id) ? pinnedColumnBorderStyle('left') : {}),
            } : isPinned === 'right' ? {
              position: 'sticky' as const,
              right: getPinnedRightOffset(header.id, columnSizing, minColumnWidth),
              top: 0,
              zIndex: 30,
              ...(isFirstPinnedRight(header.id) ? pinnedColumnBorderStyle('right') : {}),
            } : {
              position: 'sticky' as const,
              top: 0,
              zIndex: 25,
            };

            return (
              <ContextMenu key={header.id}>
                <ContextMenuTrigger asChild>
                  <th
                    className={`relative px-3 py-1.5 ${isNumericHeader ? 'text-right' : 'text-left'} font-medium text-xs
                               bg-muted border-r border-border last:border-r-0
                               text-muted-foreground whitespace-nowrap select-none overflow-hidden text-ellipsis
                               ${canSort ? 'cursor-pointer hover:text-foreground' : ''}
                               ${isPopulating ? 'opacity-60' : ''}
                               ${isEditableHeader ? 'border-b-2 border-b-amber-400/50' : 'border-b border-b-border'}`}
                    data-editable={isEditableHeader ? 'true' : undefined}
                    data-populating={isPopulating ? 'true' : undefined}
                    // Lets `lockColumnWidths` map a measured <th> back to its
                    // column by id instead of by position — the header row also
                    // carries non-column cells (the filler here, plus a leading
                    // gutter in the Table Explorer), and a positional map
                    // silently assigns each column its neighbour's width.
                    data-column-id={header.id}
                    style={{
                      ...(hasExplicitWidth
                        ? { width: header.getSize() }
                        : { minWidth: minColumnWidth, maxWidth: maxAutoColumnWidth }),
                      ...stickyStyle,
                    }}
                    onClick={() => {
                      if (!canSort) return;
                      const newState = (() => {
                        if (!sortState || sortState.columnId !== header.id) {
                          return { columnId: header.id, direction: 'asc' as const };
                        }
                        if (sortState.direction === 'asc') {
                          return { columnId: header.id, direction: 'desc' as const };
                        }
                        return null;
                      })();
                      setSortState(newState);
                      if (isWindowed) handleWindowedSortChange(newState);
                    }}
                    title={isPopulating ? 'Sort not available — results are still loading' : (canSort ? 'Click to sort' : 'Sort not available — add ORDER BY to your query')}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {/* Resize handle.
                        `flushSync` matters: TanStack's resize handler reads
                        `header.getSize()` at event time, so without a synchronous
                        commit it sees the pre-lock state and starts the drag from
                        the 150px default instead of the column's rendered width —
                        the column jumped to 150px the moment you grabbed its
                        border (measured: 50 → 150 on a 50px column). */}
                    <div
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        flushSync(() => lockColumnWidths());
                        header.getResizeHandler()(e);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        flushSync(() => lockColumnWidths());
                        header.getResizeHandler()(e);
                      }}
                      onClick={(e) => { e.stopPropagation(); }}
                      // G-3 — hover ordering. `.hover\:bg-primary\/50:hover` is
                      // (0,0,2,0) and the plain `.bg-primary` resizing class is
                      // (0,0,1,0), so hovering a handle that is ALREADY resizing
                      // used to weaken it 100% -> 50% — the same specificity trap
                      // that produced three live bugs in Wave 4. It was masked by
                      // `active:` (which ties at (0,0,2,0) and sorts later), so it
                      // was latent rather than visible; a touch drag, where
                      // `:active` does not hold, is the reachable case.
                      //
                      // Resolved in JS so exactly ONE `hover:bg-*` utility is ever
                      // emitted, per the shared `selectionAffordance` rule — the
                      // cascade never gets to arbitrate. Uses the PIN form (rather
                      // than omitting) because the handle must stay interactive
                      // while resizing. Identical in `TableExplorerGrid`.
                      className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none
                                  active:bg-primary
                                  ${header.column.getIsResizing() ? 'bg-primary hover:bg-primary' : 'hover:bg-primary/50'}`}
                    />
                  </th>
                </ContextMenuTrigger>
                {/* `onCloseAutoFocus` is prevented, as in `TableExplorerGrid`'s
                    header menu: "Add to Filter" hands the keyboard to the
                    condition draft that just opened, which the trigger would
                    otherwise take back. */}
                <ContextMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
                  {onAddToFilter && (
                    <AddToFilterItem
                      columnId={header.id}
                      onAddToFilter={onAddToFilter}
                      unavailableReason={addToFilterUnavailableReason}
                    />
                  )}
                  {isPinned ? (
                    <ContextMenuItem
                      onClick={() => unpinColumn(header.id)}
                      className="text-xs"
                    >
                      <Pin className="h-3.5 w-3.5 mr-2" />
                      Unpin column
                    </ContextMenuItem>
                  ) : (
                    <>
                      <ContextMenuItem
                        onClick={() => { lockColumnWidths(); pinColumn(header.id, 'left'); }}
                        className="text-xs"
                      >
                        <Pin className="h-3.5 w-3.5 mr-2" />
                        Pin to left
                      </ContextMenuItem>
                      <ContextMenuItem
                        onClick={() => { lockColumnWidths(); pinColumn(header.id, 'right'); }}
                        className="text-xs"
                      >
                        <Pin className="h-3.5 w-3.5 mr-2" />
                        Pin to right
                      </ContextMenuItem>
                    </>
                  )}
                  {onCopyColumnValues && (
                    <ContextMenuItem
                      onClick={() => onCopyColumnValues(header.id)}
                      className="text-xs"
                      data-testid="grid-header-copy-column-values"
                    >
                      <Copy className="h-3.5 w-3.5 mr-2" />
                      Copy values
                    </ContextMenuItem>
                  )}
                  {onProfileColumn && (
                    <ContextMenuItem
                      onClick={() => onProfileColumn(header.id)}
                      className="text-xs"
                      data-testid="grid-header-profile-column"
                    >
                      <BarChart3 className="h-3.5 w-3.5 mr-2" />
                      Profile column
                    </ContextMenuItem>
                  )}
                  {onFindUsages && (
                    <ContextMenuItem
                      onClick={() => onFindUsages(header.id)}
                      className="text-xs"
                      data-testid="grid-header-find-usages"
                    >
                      <FileSearch className="h-3.5 w-3.5 mr-2" />
                      {FIND_USAGES_MENU_LABEL}
                    </ContextMenuItem>
                  )}
                  {onShowJsonDetails && declaredJsonColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onShowJsonDetails(header.id)}
                      className="text-xs"
                      data-testid="grid-header-json-details"
                    >
                      <FileJson className="h-3.5 w-3.5 mr-2" />
                      JSON details
                    </ContextMenuItem>
                  )}
                  {onDeclareAsJson && declarableJsonColumnIds?.has(header.id) && !declaredJsonColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onDeclareAsJson(header.id)}
                      className="text-xs"
                      data-testid="grid-header-declare-json"
                    >
                      <Code2 className="h-3.5 w-3.5 mr-2" />
                      Declare as JSON
                    </ContextMenuItem>
                  )}
                  {onEnumColumnAction && declaredEnumColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onEnumColumnAction(header.id, 'declared')}
                      className="text-xs"
                      data-testid="grid-header-enum-declared"
                    >
                      <List className="h-3.5 w-3.5 mr-2" />
                      Enum details
                    </ContextMenuItem>
                  )}
                  {onEnumColumnAction && nativeEnumColumnIds?.has(header.id) && !declaredEnumColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onEnumColumnAction(header.id, 'native')}
                      className="text-xs"
                      data-testid="grid-header-enum-native"
                    >
                      <List className="h-3.5 w-3.5 mr-2" />
                      Configure enum column
                    </ContextMenuItem>
                  )}
                  {onEnumColumnAction && declarableEnumColumnIds?.has(header.id) && !declaredEnumColumnIds?.has(header.id) && !nativeEnumColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onEnumColumnAction(header.id, 'eligible')}
                      className="text-xs"
                      data-testid="grid-header-enum-eligible"
                    >
                      <ChevronDown className="h-3.5 w-3.5 mr-2" />
                      Declare as enum
                    </ContextMenuItem>
                  )}
                  {onSpatialColumnAction && declaredSpatialColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onSpatialColumnAction(header.id, 'declared')}
                      className="text-xs"
                      data-testid="grid-header-spatial-declared"
                    >
                      <MapPin className="h-3.5 w-3.5 mr-2" />
                      Coordinate details
                    </ContextMenuItem>
                  )}
                  {onSpatialColumnAction && declarableSpatialColumnIds?.has(header.id) && !declaredSpatialColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onSpatialColumnAction(header.id, 'declarable')}
                      className="text-xs"
                      data-testid="grid-header-declare-spatial"
                    >
                      <MapPin className="h-3.5 w-3.5 mr-2" />
                      Declare as coordinates…
                    </ContextMenuItem>
                  )}
                  {onEditLooseFk && looseFkColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onEditLooseFk(header.id)}
                      className="text-xs"
                      data-testid="grid-header-edit-loose-fk"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-2" />
                      Edit loose relationship
                    </ContextMenuItem>
                  )}
                  {onDeclareLooseFk && looseFkEligibleColumnIds?.has(header.id) && !looseFkColumnIds?.has(header.id) && (
                    <ContextMenuItem
                      onClick={() => onDeclareLooseFk(header.id)}
                      className="text-xs"
                      data-testid="grid-header-declare-loose-fk"
                    >
                      <Link2 className="h-3.5 w-3.5 mr-2" />
                      Declare loose relationship
                    </ContextMenuItem>
                  )}
                </ContextMenuContent>
              </ContextMenu>
            );
          })}
          {/* PP-05 — inert filler carrying the header band to the right edge.
              Deliberately NOT a TanStack column: it can't be sorted, filtered,
              resized, reordered, pinned or selected, and `getVisibleCells()`
              never yields it, so copy/export are structurally unaffected.

              `width: 100%` is what makes the slack actually land here. Under
              `table-layout: auto` the `max-width` on the data cells is inert, so
              without a greedy filler the columns stretched to absorb the pane
              (measured: 543px each on a 2-column result, filler 5px). A cell
              asking for the whole table width is served last, after every other
              column has taken its content width — so the columns stay at their
              natural size and the filler gets the remainder. Measured inert
              under `table-layout: fixed` and when the grid overflows. */}
          <th
            aria-hidden="true"
            data-grid-filler="true"
            data-testid="grid-filler-header"
            className="p-0 bg-muted border-b border-b-border select-none"
            style={{ width: '100%' }}
          />
        </tr>
      ))}
      {/* Filter row */}
      {showResultFilter && (
        <tr data-testid="result-filter-row">
          {table.getHeaderGroups()[0]?.headers.map((header, colIdx) => {
            const hasExplicitWidth = columnSizing[header.id] !== undefined;
            const isNumericFilter = numericColumnIds?.has(header.id) ?? false;
            const isPinned = header.column.getIsPinned();
            const stickyStyle = isPinned === 'left' ? {
              position: 'sticky' as const,
              left: getPinnedLeftOffset(header.id, columnSizing, minColumnWidth),
              top: headerRowHeight,
              zIndex: 30,
            } : isPinned === 'right' ? {
              position: 'sticky' as const,
              right: getPinnedRightOffset(header.id, columnSizing, minColumnWidth),
              top: headerRowHeight,
              zIndex: 30,
            } : {
              position: 'sticky' as const,
              top: headerRowHeight,
              zIndex: 25,
            };
            return (
              <th
                key={header.id}
                className="px-1 py-0.5 bg-muted border-r border-border last:border-r-0 border-b border-b-border"
                style={{
                  ...(hasExplicitWidth
                    ? { width: header.getSize() }
                    : { minWidth: minColumnWidth, maxWidth: maxAutoColumnWidth }),
                  ...stickyStyle,
                }}
              >
                {/* Extends the muted background 10px above this sticky cell so
                    any sub-pixel/layout gap between the header row and filter
                    row is filled with muted instead of showing scrolled rows.
                    Placed inside the sticky <th> so it moves with the cell. */}
                <div
                  aria-hidden
                  className="absolute left-0 right-0 bg-muted pointer-events-none"
                  style={{ top: -10, height: 10 }}
                />
                {colIdx === 0 ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      placeholder="All…"
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setColumnFilters({});
                          setGlobalFilter('');
                        }
                      }}
                      className="w-full min-w-0 h-5 px-1 text-[11px] bg-transparent border-b border-border/50 focus:border-primary focus:outline-none placeholder:text-muted-foreground/50"
                      data-testid="result-filter-global"
                    />
                    {isFilterActive && (
                      <button
                        type="button"
                        onClick={() => { setColumnFilters({}); setGlobalFilter(''); }}
                        className="shrink-0 p-0.5 rounded hover:bg-background/50 text-muted-foreground hover:text-foreground"
                        title="Clear all filters"
                        data-testid="result-filter-clear"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Filter…"
                    value={columnFilters[header.id] ?? ''}
                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, [header.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setColumnFilters({});
                        setGlobalFilter('');
                      }
                    }}
                    ref={(el) => { filterInputRefs.current[header.id] = el; }}
                    className={`w-full min-w-0 h-5 px-1 text-[11px] bg-transparent border-b border-border/50 focus:border-primary focus:outline-none placeholder:text-muted-foreground/50 ${isNumericFilter ? 'text-right' : ''}`}
                    data-testid={`result-filter-col-${header.id}`}
                  />
                )}
              </th>
            );
          })}
          {/* Same greedy filler as the header row — see the comment there. */}
          <th
            aria-hidden="true"
            data-grid-filler="true"
            data-testid="grid-filler-filter"
            className="p-0 bg-muted border-b border-b-border select-none"
            style={{ width: '100%' }}
          />
        </tr>
      )}
    </thead>
  );
});

/**
 * The header menu's "Add to Filter". Mounted only while the menu is open, so
 * the reason a column gets no condition is worked out on open. A disabled item
 * stays hoverable so its tooltip can say why (Radix's `disabled` would block
 * the hover).
 */
function AddToFilterItem({
  columnId,
  onAddToFilter,
  unavailableReason,
}: {
  columnId: string;
  onAddToFilter: (columnId: string) => void;
  unavailableReason?: (columnId: string) => string | null;
}) {
  const reason = unavailableReason?.(columnId) ?? null;
  return (
    <ContextMenuItem
      onSelect={reason ? (e) => e.preventDefault() : () => onAddToFilter(columnId)}
      title={reason ?? undefined}
      aria-disabled={reason ? 'true' : undefined}
      className={reason ? 'text-xs cursor-not-allowed opacity-50 hover:bg-transparent hover:text-foreground focus:bg-transparent' : 'text-xs'}
      data-testid="grid-header-add-to-filter"
      data-disabled-reason={reason ?? undefined}
    >
      <Filter className="h-3.5 w-3.5 mr-2" />
      Add to Filter
    </ContextMenuItem>
  );
}
